<template>
    <CompassModal
        v-model:visible="showModal"
        size="small"
        class="staff-form-modal"
        :title="modalTitle"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="emit('close')"
    >
        <template #default>
            <StaffFormLoader v-if="isLoading" />
            <div v-else class="staff-form-modal__form">
                <CompassFormItem
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.civility')"
                    required
                    :error="errors.civility"
                >
                    <CompassRadioGroup
                        v-model="form.civility"
                        label=""
                        direction="horizontal"
                        @update:modelValue="errors.civility = ''"
                    >
                        <CompassRadio
                            v-for="civility in civilities"
                            :key="civility.label"
                            :modelValue="civility.value"
                            :label="civility.label"
                            :error="!!errors.civility"
                        />
                    </CompassRadioGroup>
                </CompassFormItem>
                <div class="staff-form-modal__columns">
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.firstname')"
                        required
                        :error="errors.firstname"
                    >
                        <CompassInput
                            v-model="form.firstname"
                            :error="!!errors.firstname"
                            @input="errors.firstname = ''"
                            @blur="checkName('firstname')"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.lastname')"
                        required
                        :error="errors.lastname"
                    >
                        <CompassInput
                            v-model="form.lastname"
                            :error="!!errors.lastname"
                            @input="errors.lastname = ''"
                            @blur="checkName('lastname')"
                        />
                    </CompassFormItem>
                </div>
                <CompassFormItem
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.email')"
                    required
                    :error="errors.email"
                >
                    <CompassInput
                        v-model="form.email"
                        type="email"
                        :error="!!errors.email"
                        @input="errors.email = ''"
                        @blur="checkEmail"
                    />
                </CompassFormItem>
                <div class="staff-form-modal__columns">
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.mobile')"
                        :error="
                            errors.mobile_number
                                ? $t.__('common.labels.please_enter_a_valid_phone_number')
                                : ''
                        "
                    >
                        <CompassTelInput
                            :modelValue="form.mobile_number"
                            :defaultCountry="defaultCountryCode"
                            :error="!!errors.mobile_number"
                            menuClass="staff-form-modal__dropdown"
                            @update:modelValue="errors.mobile_number = ''"
                            @blur="($event) => handleTelInputBlur($event, 'mobile_number')"
                            @validate="(value) => updatePhoneNumber(value, 'mobile_number')"
                            @country-changed="handleCountryChange($event, 'mobile_number')"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.telephone')"
                        :error="
                            errors.phone_number
                                ? $t.__('common.labels.please_enter_a_valid_phone_number')
                                : ''
                        "
                    >
                        <CompassTelInput
                            :modelValue="form.phone_number"
                            :defaultCountry="defaultCountryCode"
                            :error="!!errors.phone_number"
                            menuClass="staff-form-modal__dropdown"
                            @update:modelValue="errors.phone_number = ''"
                            @blur="($event) => handleTelInputBlur($event, 'phone_number')"
                            @validate="(value) => updatePhoneNumber(value, 'phone_number')"
                            @country-changed="handleCountryChange($event, 'phone_number')"
                        />
                    </CompassFormItem>
                </div>
                <div v-if="!isSellsyCorp" class="staff-form-modal__columns">
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.team')"
                        required
                        :error="errors.team"
                    >
                        <CompassSelect
                            v-model="form.team"
                            menuClass="staff-form-modal__dropdown"
                            :error="!!errors.team"
                            :placeholder="$t.__('common.actions.select')"
                            @update:modelValue="resetJob"
                        >
                            <CompassDropdownOption
                                v-for="item in teamsList"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                    <CompassFormItem
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.job')"
                        required
                        :error="errors.job"
                    >
                        <CompassSelect
                            v-model="form.job"
                            :disabled="!form.team"
                            :error="!!errors.job"
                            :placeholder="$t.__('common.actions.select')"
                            menuClass="staff-form-modal__dropdown"
                            @update:modelValue="removeJobError"
                        >
                            <CompassDropdownOption
                                v-for="item in jobsList"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                </div>
                <CompassFormItem
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.profile')"
                    required
                    :info="$t.__('settings.StaffSettings.StaffForm.labels.profile.info')"
                >
                    <CompassSelect v-model="form.profile" menuClass="staff-form-modal__dropdown">
                        <CompassDropdownOption
                            v-for="item in profilesList"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <h3 class="staff-form-modal__subtitle typographies-heading-h3">
                    {{ $t.__('settings.StaffSettings.StaffForm.subtitle') }}
                </h3>
                <CompassFormItem
                    v-if="hasLicenseManagement"
                    required
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.license')"
                    :error="errors.license"
                >
                    <CompassSelect
                        v-model="form.licenses[0].id"
                        menuClass="staff-form-modal__dropdown"
                        :error="!!errors.license"
                        :placeholder="
                            $t.__('settings.StaffSettings.StaffForm.labels.license.placeholder')
                        "
                        @update:modelValue="errors.license = ''"
                    >
                        <CompassDropdownOption
                            v-for="item in availableLicenses"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                            :disabled="!!item.disabled && item.value !== form.licenses[0].id"
                        />
                    </CompassSelect>
                    <CompassLink
                        :label="$t.__('settings.StaffSettings.StaffForm.labels.license.link')"
                        target="_blank"
                        link="https://go.sellsy.com/offres"
                        size="small"
                    />
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.timezone')"
                    required
                    :error="errors.timezone"
                >
                    <CompassSelect
                        v-model="form.timezone"
                        filterable
                        menuClass="staff-form-modal__dropdown"
                        :error="!!errors.timezone"
                    >
                        <CompassDropdownOption
                            v-for="item in timezones"
                            :key="item.value"
                            :label="item.name"
                            :value="item.value"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.language')"
                    required
                    :error="errors.language"
                >
                    <CompassSelect
                        v-model="form.language"
                        menuClass="staff-form-modal__dropdown"
                        :error="!!errors.language"
                    >
                        <CompassDropdownOption
                            v-for="item in languages"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassCheckbox
                    v-if="!isEdition"
                    v-model="form.first_connection_onboarding"
                    :label="$t.__('settings.StaffSettings.StaffForm.labels.onboarding')"
                    @change="(value) => (form.first_connection_onboarding = value)"
                />
            </div>
        </template>
    </CompassModal>
</template>
<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import StaffApi from 'api/staff/staff';
import Civilities from 'Config/authenticated/Civilities';
import Timezones from 'Config/authenticated/Timezones';
import { staffLanguages } from 'Config/authenticated/Languages';
import { useStore } from 'vuex';
import StaffFormLoader from 'vcomponents/settings/staff/StaffFormLoader.vue';
import { validatePhoneNumber } from 'Helpers/validator/PhoneNumberValidator';
import { validateEmail } from 'Helpers/validator/EmailValidator';
import ErrorsHandler from 'Services/ErrorsHandler';

const props = defineProps({
    staffid: {
        type: [Number, Boolean],
        default: false,
    },
    isSellsyCorp: {
        type: Boolean,
        default: false,
    },
});

// Store
const store = useStore();

// Injected
const $t = inject('$t');
const $pushToast = inject('$pushToast');

// Emits
const emit = defineEmits(['close']);

// Consts
const cancelButtonProps = {
    label: $t.__('common.actions.cancel'),
};
const civilities = Civilities;
const languages = staffLanguages;
const timezones = Timezones;

// Refs
const showModal = ref(false);
const isLoading = ref(true);
const isSaving = ref(false);
const teamsList = ref([]);
const profilesList = ref([]);
const form = ref({
    civility: '',
    firstname: '',
    lastname: '',
    email: '',
    mobile_number: '',
    phone_number: '',
    fax_number: '',
    team: '',
    job: '',
    first_connection_onboarding: false,
    profile: null,
    licenses: [{ id: '' }],
    timezone: 'Europe/Paris',
    language: 'fr',
    totp_code: null
});
const errors = ref({
    civility: '',
    firstname: '',
    lastname: '',
    email: '',
    mobile_number: false,
    phone_number: false,
    team: '',
    job: '',
    profile: null,
    licenses: '',
    timezone: '',
    language: '',
});
const country = ref({
    mobile_number: null,
    phone_number: null,
});

// Computed
const isEdition = computed(() => !!props.staffid);
const modalTitle = computed(() =>
    isEdition.value
        ? $t.__('settings.StaffSettings.StaffForm.edit.title')
        : $t.__('settings.StaffSettings.StaffForm.add.title'),
);
const primaryAction = computed(() => ({
    label: isEdition.value
        ? $t.__('settings.StaffSettings.StaffForm.edit.button')
        : $t.__('settings.StaffSettings.StaffForm.add.button'),
    loading: isLoading.value || isSaving.value,
    action: validateBeforeSaving,
}));
const uiLang = computed(() => {
    return store.state.corp.uiLang;
});
const jobsList = computed(() => {
    if (form.value.team) {
        return teamsList.value.find((team) => team.id === form.value.team)?.jobs;
    }
    return [];
});
const hasLicenseManagement = computed(() => store.state.corp.hasLicenseManagement);
const availableLicenses = computed(() => store.state.subscriptionManager.availableLicenses);
const defaultCountryCode = computed(() => store.state.corp.countryCode);

// Methods
const preparePayload = () => {
    const payload = JSON.parse(JSON.stringify(form.value));
    if (!hasLicenseManagement.value) {
        delete payload.licenses;
    }
    if (hasLicenseManagement.value && payload.licenses[0].id === 'null') {
        payload.licenses = [];
    }
    if (isEdition.value) {
        delete payload.created;
        delete payload.status;
        delete payload.avatar;
        delete payload.groups;
    }
    if (payload.totp_code === null) {
        delete payload.totp_code;
    }

    return payload;
};

const refreshStaffListAndNotify = (successMessageKey) => {
    StaffApi.getStaffListForStaffPage()
        .then((response) => {
            EventBus.emit('staffSettings:staffListUpdated', response);
            $pushToast({
                type: 'success',
                icon: true,
                text: $t.__(successMessageKey),
            });
        })
        .catch((error) => {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('settings.StaffSettings.StaffForm.refresh.error'),
            });
            ErrorsHandler.logException(error);
        });
};

const parseApiErrors = (apiErrors) => {
    const parsedErrors = {};
    if (!apiErrors) {
        return parsedErrors;
    }
    for (const [field, message] of Object.entries(apiErrors)) {
        if (typeof message === 'string') {
            parsedErrors[field] = message;
        } else if (Array.isArray(message) && message.length > 0) {
            parsedErrors[field] = message[0];
        }
    }
    return parsedErrors;
};

const saveStaff = () => {
    isSaving.value = true;
    const payload = preparePayload();
    const apiCall = isEdition.value ? StaffApi.updateStaff : StaffApi.createStaff;
    const successKey = isEdition.value
        ? 'settings.StaffSettings.StaffForm.update.success'
        : 'settings.StaffSettings.StaffForm.create.success';

    apiCall(payload)
        .then(() => {
            refreshStaffListAndNotify(successKey);
            emit('close');
        })
        .catch((error) => {
            //Check two factor error
            if (error.response.data.error.code === 401 && error.response.data.error.message === 'Two factor authentication required') {
                store.dispatch('popupPanel/open', {
                    key: 'otpCodeModal',
                    props: {
                        callback: (otpCode) => {
                            form.value.totp_code = otpCode;
                            saveStaff();
                        },
                    },
                });
                return;
            }

            errors.value = parseApiErrors(error.response.data.error.details);
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('settings.StaffSettings.StaffForm.createOrUpdate.error'),
            });
            ErrorsHandler.logException(error);
        })
        .finally(() => {
            isSaving.value = false;
            form.value.totp_code = null;
        });
};
const validateBeforeSaving = () => {
    checkEmail();
    checkName('firstname');
    checkName('lastname');

    // Validate required fields
    const requiredMessage = $t.__('common.labels.this_field_is_required');
    const fieldsToValidate = props.isSellsyCorp
        ? ['civility', 'profile', 'timezone', 'language']
        : ['civility', 'team', 'job', 'profile', 'timezone', 'language'];

    fieldsToValidate.forEach((field) => {
        if (!form.value[field]) {
            errors.value[field] = requiredMessage;
        }
    });

    if (hasLicenseManagement.value && !form.value.licenses[0].id) {
        errors.value.license = requiredMessage;
    }

    if (!Object.values(errors.value).some((error) => error)) {
        handleSave();
    }
};
const handleSave = () => {
    isSaving.value = true;
    saveStaff();
};
const initPrivilegeProfileInput = () => {
    if (!isEdition.value && profilesList.value.some((profile) => !profile.is_administrator)) {
        form.value.profile = profilesList.value.filter(
            (profile) => !profile.is_administrator,
        )[0].id;
    }
};
const resetJob = () => {
    form.value.job = '';
    errors.value.team = '';
};
const removeJobError = () => {
    if (form.value.job) {
        errors.value.job = '';
    }
};
const handleCountryChange = ($event, field) => {
    country.value[field] = $event;
};
const updatePhoneNumber = (value, field) => {
    form.value[field] = value.valid ? value.formatted : '';
};
const handleTelInputBlur = ($event, field) => {
    if (!$event || $event.target.closest('.compass-select-flag')) {
        errors.value[field] = false;
        return;
    }
    const inputValue = $event.target.value;
    if (!inputValue || inputValue.replace('+', '') === country.value[field].dialCode) {
        errors.value[field] = false;
        return;
    }
    errors.value[field] = !validatePhoneNumber(form.value[field], country.value[field].iso2);
};
const checkEmail = () => {
    if (!form.value.email) {
        errors.value.email = $t.__('common.labels.this_field_is_required');
        return;
    }

    if (!validateEmail(form.value.email)) {
        errors.value.email = $t.__('common.labels.please_enter_a_valid_email_address');
        return;
    }
    errors.value.email = '';
};
const checkName = (field) => {
    if (!form.value[field]) {
        errors.value[field] = $t.__('common.labels.this_field_is_required');
        return;
    }
    if (form.value[field].length > 200) {
        errors.value[field] = $t.__('settings.StaffSettings.StaffForm.errors.name_length');
        return;
    }
    errors.value[field] = '';
};
const hydrateStaff = (response) => {
    form.value = { ...form.value, ...response.staff.data };
    form.value.profile = parseInt(response.staff.data.profile);
    form.value.team = response.staff.data.team ? parseInt(response.staff.data.team.id) : null;
    form.value.job = response.staff.data.job ? parseInt(response.staff.data.job.id) : null;
    form.value.licenses[0].id = response.staff.data._embed.licenses
        ? response.staff.data._embed.licenses[0].id
        : 'null';
    delete form.value._embed;
};

// Lifecycle
onMounted(async () => {
    showModal.value = true;
});

Promise.all([StaffApi.getTeamsList(), StaffApi.getProfilesList('name')])
    .then(async ([teams, profiles]) => {
        teamsList.value = teams.data.data;
        profilesList.value = profiles.data.data;
        initPrivilegeProfileInput();

        if (hasLicenseManagement.value) {
            await store.dispatch('subscriptionManager/getSubscription');
        }
        if (isEdition.value) {
            return StaffApi.getStaff(props.staffid)
                .then((response) => {
                    hydrateStaff(response);
                })
                .catch((error) => {
                    emit('close');
                    $pushToast({
                        type: 'error',
                        icon: true,
                        text: $t.__('settings.StaffSettings.StaffForm.fetch.error'),
                    });
                    ErrorsHandler.logException(error);
                });
        }
    })
    .catch((error) => {
        emit('close');
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('settings.StaffSettings.StaffForm.fetch.error'),
        });
        ErrorsHandler.logException(error);
    })
    .finally(() => {
        isLoading.value = false;
    });

form.value.language = uiLang.value;
</script>
<style lang="scss">
#compass-dropdown-container {
    .staff-form-modal__dropdown {
        z-index: 1000;
    }
}
</style>
<style lang="scss" scoped>
.staff-form-modal {
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
    }

    &__columns {
        display: flex;
        gap: var(--spacing-3);

        &:deep(.compass-form-item) {
            flex: 1;
            min-width: 0;
        }

        &:deep(.compass-tel-input .compass-input) {
            min-width: 0;
        }
    }

    &:deep(.compass-link) {
        align-self: start;
        order: 3;
    }

    &__subtitle {
        margin: 0 0 calc(var(--spacing-1) * -1);
    }
}
</style>
